import { useState, useCallback, useEffect } from "react";
import { scanAIVisibility, type ScanResult } from "@/lib/api/ai-visibility";
import { scanPlatformVisibility, type PlatformVisibilityResult } from "@/lib/api/platform-visibility";
import { scanGoogleSearchVisibility, type GoogleSearchResult } from "@/lib/api/google-search-visibility";
import { isDebugMode } from "@/lib/analytics";
import { createMockAIResult, createMockPlatformResult, createMockGoogleResult, MOCK_DELAY_MS } from "@/lib/mock/scanMockData";
import { supabase } from "@/integrations/supabase/client";

// Persist debug flag to sessionStorage on page load (survives navigation)
if (typeof window !== 'undefined') {
  const params = new URLSearchParams(window.location.search);
  const debugParam = params.get('debug');
  
  if (debugParam === 'true') {
    sessionStorage.setItem('debug_mode_session', 'true');
  } else if (debugParam === 'false') {
    sessionStorage.removeItem('debug_mode_session');
  }
}

// Unified scan enabled for all users (50% cost savings via single API call)
const checkUnifiedScanFlag = (): boolean => true;

export type ScanStatus = "idle" | "loading" | "success" | "error";

export interface ScanState {
  aiResult: ScanResult | null;
  platformResult: PlatformVisibilityResult | null;
  googleResult: GoogleSearchResult | null;
  aiStatus: ScanStatus;
  platformStatus: ScanStatus;
  googleStatus: ScanStatus;
  isComplete: boolean;
  isFromCache: boolean;
}

interface CacheEntry {
  brandName: string;
  keyword: string;
  timestamp: number;
  aiResult: ScanResult | null;
  platformResult: PlatformVisibilityResult | null;
  googleResult: GoogleSearchResult | null;
}

const CACHE_KEY = "ailens_v2.2_scan_cache";
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

function getCache(brandName: string, keyword: string): CacheEntry | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    
    const entry: CacheEntry = JSON.parse(cached);
    
    // Check TTL
    if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    
    // Case-insensitive match
    if (
      entry.brandName.toLowerCase() === brandName.toLowerCase() &&
      entry.keyword.toLowerCase() === keyword.toLowerCase()
    ) {
      return entry;
    }
    
    return null;
  } catch {
    return null;
  }
}

function setCache(
  brandName: string,
  keyword: string,
  aiResult: ScanResult | null,
  platformResult: PlatformVisibilityResult | null,
  googleResult: GoogleSearchResult | null
): void {
  try {
    const entry: CacheEntry = {
      brandName,
      keyword,
      timestamp: Date.now(),
      aiResult,
      platformResult,
      googleResult,
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    // Ignore storage errors
  }
}

export interface InitialScanData {
  aiResult?: ScanResult | null;
  platformResult?: PlatformVisibilityResult | null;
  googleResult?: GoogleSearchResult | null;
}

export function useScanProgress(
  brandName: string, 
  keyword: string,
  initialData?: InitialScanData
) {
  const [state, setState] = useState<ScanState>(() => {
    // If initial data provided (from navigation state), skip loading
    if (initialData?.aiResult) {
      return {
        aiResult: initialData.aiResult,
        platformResult: initialData.platformResult || null,
        googleResult: initialData.googleResult || null,
        aiStatus: "success",
        platformStatus: initialData.platformResult ? "success" : "idle",
        googleStatus: initialData.googleResult ? "success" : "idle",
        isComplete: true,
        isFromCache: true, // Treat navigation state like cache
      };
    }
    
    return {
      aiResult: null,
      platformResult: null,
      googleResult: null,
      aiStatus: "idle",
      platformStatus: "idle",
      googleStatus: "idle",
      isComplete: false,
      isFromCache: false,
    };
  });

  const [hasStarted, setHasStarted] = useState(!!initialData?.aiResult);

  const startScan = useCallback(async () => {
    if (!brandName || !keyword || hasStarted) return;

    const debugMode = isDebugMode();
    
    if (debugMode) {
      
      setHasStarted(true);
      
      // Set all to loading first to test loaders/animations
      setState((prev) => ({
        ...prev,
        aiStatus: "loading",
        platformStatus: "loading",
        googleStatus: "loading",
      }));
      
      // Simulate realistic API delay to test loading states
      await new Promise(resolve => setTimeout(resolve, MOCK_DELAY_MS));
      
      // Return mock data with user's actual input
      const mockAI = createMockAIResult(brandName, keyword);
      const mockPlatform = createMockPlatformResult(brandName, keyword);
      const mockGoogle = createMockGoogleResult(brandName, keyword);
      
      setState({
        aiResult: mockAI,
        platformResult: mockPlatform,
        googleResult: mockGoogle,
        aiStatus: "success",
        platformStatus: "success",
        googleStatus: "success",
        isComplete: true,
        isFromCache: false,
      });
      return;
    }

    // Check cache first (only in non-debug mode)
    const cached = getCache(brandName, keyword);
    if (cached) {
      setState({
        aiResult: cached.aiResult,
        platformResult: cached.platformResult,
        googleResult: cached.googleResult,
        aiStatus: cached.aiResult ? "success" : "error",
        platformStatus: cached.platformResult ? "success" : "error",
        googleStatus: cached.googleResult ? "success" : "error",
        isComplete: true,
        isFromCache: true,
      });
      return;
    }

    setHasStarted(true);

    // Set all to loading
    setState((prev) => ({
      ...prev,
      aiStatus: "loading",
      platformStatus: "loading",
      googleStatus: "loading",
    }));

    // Unified scan is now the default for all users
    const useUnifiedScan = checkUnifiedScanFlag();

    // Run scans - conditional based on unified flag
    let aiPromise: Promise<ScanResult | null>;
    let platformPromise: Promise<PlatformVisibilityResult | null>;

    if (useUnifiedScan) {
      const unifiedPromise = supabase.functions.invoke('unified-ai-visibility-scan', {
        body: { brandName, targetKeyword: keyword }
      });

      // Transform unified response to match existing AI result structure
      // V2.1: Now includes Discovery Mode fields
      aiPromise = unifiedPromise
        .then((response) => {
          if (response.error) throw response.error;
          const data = response.data;
          
          if (!data?.overallAnalysis) throw new Error('Invalid unified response');
          
          // Map to existing ScanResult structure with V2.1 fields
          const aiResult: ScanResult = {
            success: true,
            brandName: data.brandName,
            targetKeyword: data.targetKeyword,
            analysis: {
              visibilityScore: data.overallAnalysis.visibilityScore,
              keyFindings: data.overallAnalysis.keyFindings,
              competitorMentions: data.overallAnalysis.competitorMentions,
              recommendations: data.overallAnalysis.recommendations,
              summary: data.overallAnalysis.summary,
            },
            // V2.1 Discovery Mode fields
            aiScore: data.aiScore,
            queryType: data.queryType,
            queryUsed: data.queryUsed,
            rawResponseSnippet: data.rawResponseSnippet,
            competitorMentions: data.competitorMentions,
          };
          
          setState((prev) => ({
            ...prev,
            aiResult,
            aiStatus: "success",
          }));
          return aiResult;
        })
        .catch((error) => {
          console.error('🧪 [UNIFIED] AI result error:', error);
          setState((prev) => ({ ...prev, aiStatus: "error" }));
          return null;
        });

      // Transform unified response to match existing platform result structure
      platformPromise = unifiedPromise
        .then((response) => {
          if (response.error) throw response.error;
          const data = response.data;
          if (!data?.platformAnalysis) throw new Error('Invalid unified response');
          
          // Map to existing PlatformVisibilityResult structure
          const platformResult: PlatformVisibilityResult = {
            success: true,
            brandName: data.brandName,
            targetKeyword: data.targetKeyword,
            platforms: data.platformAnalysis.platforms,
            summary: data.platformAnalysis.summary,
          };
          
          setState((prev) => ({
            ...prev,
            platformResult,
            platformStatus: "success",
          }));
          return platformResult;
        })
        .catch((error) => {
          console.error('🧪 [UNIFIED] Platform result error:', error);
          setState((prev) => ({ ...prev, platformStatus: "error" }));
          return null;
        });

    } else {
      // DEFAULT: Use existing separate API calls (current production behavior)
      aiPromise = scanAIVisibility(brandName, keyword)
        .then((result) => {
          setState((prev) => ({
            ...prev,
            aiResult: result,
            aiStatus: "success",
          }));
          return result;
        })
        .catch(() => {
          setState((prev) => ({ ...prev, aiStatus: "error" }));
          return null;
        });

      platformPromise = scanPlatformVisibility(brandName, keyword)
        .then((result) => {
          setState((prev) => ({
            ...prev,
            platformResult: result,
            platformStatus: "success",
          }));
          return result;
        })
        .catch(() => {
          setState((prev) => ({ ...prev, platformStatus: "error" }));
          return null;
        });
    }

    // Google search call remains unchanged (always uses existing function)
    const googlePromise = scanGoogleSearchVisibility(brandName, keyword)
      .then((result) => {
        setState((prev) => ({
          ...prev,
          googleResult: result,
          googleStatus: "success",
        }));
        return result;
      })
      .catch(() => {
        setState((prev) => ({ ...prev, googleStatus: "error" }));
        return null;
      });

    // Wait for all to complete, then mark as complete and cache
    const [ai, platform, google] = await Promise.all([
      aiPromise,
      platformPromise,
      googlePromise,
    ]);

    // Cache results
    setCache(brandName, keyword, ai, platform, google);

    setState((prev) => ({ ...prev, isComplete: true, isFromCache: false }));
  }, [brandName, keyword, hasStarted]);

  // Auto-start scan when component mounts with valid inputs
  useEffect(() => {
    if (brandName && keyword && !hasStarted) {
      startScan();
    }
  }, [brandName, keyword, hasStarted, startScan]);

  return state;
}
